import { Fragment, FormEvent, useEffect, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { getApiErrorMessage, portalApi } from '../../api/client';
import type { PayoutRequest } from '../../api/types';
import { FinancialGuard } from '../../components/Layout/FinancialGuard';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Badge } from '../../components/ui/Badge';
import { TotpInput } from '../../components/ui/TotpInput';
import { useAuth } from '../../context/AuthContext';
import { formatAmount, formatDate } from '../../utils/format';

export function PayoutsPage() {
  const { merchant, balances } = useAuth();
  const [list, setList] = useState<PayoutRequest[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [method, setMethod] = useState<'fiat' | 'crypto'>('fiat');
  const [amount, setAmount] = useState('');
  const [totp, setTotp] = useState('');
  const [fiat, setFiat] = useState({ beneficiary_name: '', bank_name: '', bank_swift: '', bank_iban: '' });
  const [crypto, setCrypto] = useState({ crypto_network: 'TRC-20', crypto_address: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const load = () => {
    void portalApi.listPayouts().then((r) => setList(r.payout_requests ?? []));
  };

  useEffect(() => {
    load();
  }, []);

  const amountNum = Number(amount);
  const fee = amountNum * 0.01;
  const net = amountNum - fee;
  const available = Number(balances?.available_balance ?? 0);
  const canSubmit =
    merchant?.status === 'active' &&
    amountNum > 0 &&
    amountNum <= available &&
    totp.length === 6;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const body =
        method === 'fiat'
          ? { method: 'fiat', amount_display: amountNum, totp_code: totp, ...fiat }
          : { method: 'crypto', amount_display: amountNum, totp_code: totp, ...crypto };
      const res = await portalApi.createPayout(body);
      setSuccess(`Payout requested. Net: ${res.net_display} (fee ${res.fee_display})`);
      setAmount('');
      setTotp('');
      load();
    } catch (err) {
      setError(getApiErrorMessage(err));
    }
  };

  return (
    <FinancialGuard>
      <div className="space-y-6">
        <h2 className="font-display text-2xl">Payouts</h2>
        <div className="grid lg:grid-cols-2 gap-6">
          <Card title="New payout request">
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="flex gap-2">
                <button type="button" onClick={() => setMethod('fiat')} className={`flex-1 py-2 rounded-lg text-sm border ${method === 'fiat' ? 'border-onyx-gold text-onyx-gold' : 'border-onyx-border'}`}>Fiat (IBAN)</button>
                <button type="button" onClick={() => setMethod('crypto')} className={`flex-1 py-2 rounded-lg text-sm border ${method === 'crypto' ? 'border-onyx-gold text-onyx-gold' : 'border-onyx-border'}`}>Crypto</button>
              </div>
              <Input type="number" step="0.01" placeholder="Amount (USDT)" value={amount} onChange={(e) => setAmount(e.target.value)} required />
              <p className="text-xs text-onyx-muted">
                Gateway fee 1% · Net ≈ {Number.isFinite(net) ? net.toFixed(2) : '0'} · Available{' '}
                {formatAmount(available)}
              </p>
              {method === 'fiat' ? (
                <>
                  <Input placeholder="Beneficiary name" value={fiat.beneficiary_name} onChange={(e) => setFiat({ ...fiat, beneficiary_name: e.target.value })} required />
                  <Input placeholder="Bank name" value={fiat.bank_name} onChange={(e) => setFiat({ ...fiat, bank_name: e.target.value })} required />
                  <Input placeholder="SWIFT" value={fiat.bank_swift} onChange={(e) => setFiat({ ...fiat, bank_swift: e.target.value })} required />
                  <Input placeholder="IBAN" value={fiat.bank_iban} onChange={(e) => setFiat({ ...fiat, bank_iban: e.target.value })} required />
                </>
              ) : (
                <>
                  <select className="input-field" value={crypto.crypto_network} onChange={(e) => setCrypto({ ...crypto, crypto_network: e.target.value })}>
                    <option value="TRC-20">TRC-20</option>
                    <option value="ERC-20">ERC-20</option>
                    <option value="SPL">SPL (Solana)</option>
                  </select>
                  <Input placeholder="Wallet address" value={crypto.crypto_address} onChange={(e) => setCrypto({ ...crypto, crypto_address: e.target.value })} required />
                </>
              )}
              <div>
                <p className="text-sm text-onyx-muted mb-2">2FA code (required if enabled)</p>
                <TotpInput value={totp} onChange={setTotp} />
              </div>
              {error ? <p className="text-sm text-onyx-danger">{error}</p> : null}
              {success ? <p className="text-sm text-onyx-success">{success}</p> : null}
              <Button type="submit" disabled={!canSubmit} className="w-full">
                Submit payout
              </Button>
              {!canSubmit && amountNum > 0 ? (
                <p className="text-xs text-onyx-warn">
                  Requires active account, sufficient balance, and 6-digit 2FA.
                </p>
              ) : null}
            </form>
          </Card>
          <Card title="Payout history">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((p) => (
                  <Fragment key={p.id}>
                    <tr className="cursor-pointer hover:bg-onyx-elevated/50" onClick={() => setExpanded(expanded === p.id ? null : p.id)}>
                      <td className="font-mono text-xs">{p.id.slice(0, 8)}…</td>
                      <td>{formatAmount(p.amount_display, p.currency)}</td>
                      <td><Badge status={p.status} /></td>
                      <td>{expanded === p.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</td>
                    </tr>
                    {expanded === p.id ? (
                      <tr key={`${p.id}-d`}>
                        <td colSpan={4} className="text-xs text-onyx-muted space-y-1">
                          <p>Method: {p.payout_method ?? '—'}</p>
                          <p>Created: {formatDate(p.created_at)}</p>
                          <p>Executed: {formatDate(p.executed_at)}</p>
                          {p.beneficiary_name ? <p>Beneficiary: {p.beneficiary_name}</p> : null}
                          {p.bank_iban ? <p>IBAN: {p.bank_iban}</p> : null}
                          {p.crypto_address ? <p>{p.crypto_network}: {p.crypto_address}</p> : null}
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      </div>
    </FinancialGuard>
  );
}
